import { useMemo } from "react";
import { LifegridWordmark } from "@/components/Logo";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Menu, Volume2, VolumeX, Accessibility, LogIn, LogOut } from "lucide-react";
import { Link } from "react-router-dom";

export function Navbar({ onMenu }) {
  const { voiceOn, setVoiceOn, a11yLarge, setA11yLarge } = useApp();
  const { user, signIn, signOut, loading } = useAuth();
  const time = useMemo(() => new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }), []);
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 backdrop-blur-2xl bg-[color:var(--lg-void)]/85">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-home-link">
          <LifegridWordmark subtitle="OPERATIONAL" />
          <span className="ml-3 live-dot" aria-hidden />
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-[11px] font-mono-tac uppercase tracking-[0.22em] text-[color:var(--lg-text-2)]">
          <a href="#instant" className="hover:text-[color:var(--lg-lime)]" data-testid="nav-link-instant">Instant</a>
          <a href="#one-minute" className="hover:text-[color:var(--lg-lime)]" data-testid="nav-link-one-minute">One&nbsp;Minute</a>
          <a href="#interpreter" className="hover:text-[color:var(--lg-lime)]" data-testid="nav-link-interpreter">Interpreter</a>
          <a href="#life-card" className="hover:text-[color:var(--lg-lime)]" data-testid="nav-link-life-card">Life&nbsp;Card</a>
          <a href="#map" className="hover:text-[color:var(--lg-lime)]" data-testid="nav-link-map">Map</a>
        </nav>

        <div className="flex items-center gap-2">
          <span className="hidden lg:inline font-mono-tac text-[10px] tracking-[0.22em] text-[color:var(--lg-text-3)]">
            LOCAL {time}
          </span>
          <button
            className="btn-tactile ghost !py-2 !px-3"
            onClick={() => setVoiceOn(!voiceOn)}
            title="Voice mode"
            data-testid="nav-voice-toggle"
            aria-pressed={voiceOn}
          >
            {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <button
            className="btn-tactile ghost !py-2 !px-3"
            onClick={() => setA11yLarge(!a11yLarge)}
            title="Accessibility mode"
            data-testid="nav-a11y-toggle"
            aria-pressed={a11yLarge}
          >
            <Accessibility size={16} />
          </button>

          {!loading && !user && (
            <button className="btn-tactile primary !py-2 !px-3 hidden sm:inline-flex" onClick={signIn} data-testid="nav-signin-button">
              <LogIn size={14} /> Sign in
            </button>
          )}
          {!loading && user && (
            <div className="flex items-center gap-2" data-testid="nav-user">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="h-8 w-8 rounded-full border border-white/20"
                  data-testid="nav-user-avatar"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-[color:var(--lg-lime)] text-black font-bold flex items-center justify-center">
                  {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <button className="btn-tactile ghost !py-2 !px-3" onClick={signOut} title="Sign out" data-testid="nav-signout-button">
                <LogOut size={14} />
              </button>
            </div>
          )}

          <button className="md:hidden btn-tactile ghost !py-2 !px-3" onClick={onMenu} data-testid="nav-menu-button">
            <Menu size={16} />
          </button>
        </div>
      </div>
    </header>
  );
}
